import React, { useState } from 'react';
import { useEffect } from "react"
import { ActivityIndicator, FlatList, useWindowDimensions, View } from "react-native";
import type { EventProps, LeagueProps, MarketProps, MatchProps, OrderProps, OrderStatsProps, TournamentProps } from '../../../types';
import { MarketComponentApi } from '../../api';
import Colors from '../../../constants/colors';
import { MyOrdersHelpers } from './api';
import ContestOrderStatsCard from '../ContestOrderStatsCard';
import SocketManager from '../../../Socket';
import { Text } from '../../../Components';

type MyOrderListProps = {
    player_id:string,
    access_token?:string,
    distinct_id?:string,
    market_type:'FOR_MONEY'|'FREE',
}

const MyOrderList = ({ player_id, market_type, access_token, distinct_id }:MyOrderListProps) => {
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_order, setSocketOrder ] = useState<OrderProps|undefined>(undefined);
    const [ module_data, setModuleData ] = useState<{
        loading:boolean,
        leagues:LeagueProps[],
        orders:OrderProps[],
        markets:MarketProps[],
        events:EventProps[],
        tournaments:TournamentProps[],
        matches:MatchProps[]
    }>({
        loading:false,
        leagues:[],
        orders:[],
        markets: [],
        tournaments:[],
        matches: [],
        events: []
    });

    const { loading, orders, leagues, tournaments, matches, events, markets } = module_data;
    const { width } = useWindowDimensions();
    const hedges = MyOrdersHelpers.getHedgesFromPositions(orders, markets)

    useEffect(() => {
        MarketComponentApi.setEnvironment();
        getDataFromServer();
    },[]);

    //Everytime an order is added to socket order - we update the local data
    useEffect(() => {
        if(!socket_order){ return }
        updateFromSocket(socket_order)
    },[socket_order])

    const updateFromSocket = async(order:OrderProps) => {
        const { event_ids, tournament_ids, match_ids } = MyOrdersHelpers.getContestsFromOrders([order]);
        let es = await MarketComponentApi.getEventsByEventIds(event_ids);
        let tourneys = await MarketComponentApi.getTournamentsByTournamentIds(tournament_ids);
        let mtchs = await MarketComponentApi.getMatchesByMatchIds(match_ids);
        setModuleData({
            ...module_data,
            orders: orders.filter(o => o.order_id != order.order_id).concat(order),
            events: events.filter(e => !es.find(ne => ne.event_id == e.event_id)).concat(es),
            tournaments: tournaments.filter(t => !tourneys.find(nt => nt.tournament_id == t.tournament_id)).concat(tourneys),
            matches: matches.filter(m => !mtchs.find(nm => nm.match_id == m.match_id)).concat(mtchs)
        });
    }

    const getDataFromServer = async() => {
        setModuleData({ ...module_data, loading:true });
        let my_orders = await MarketComponentApi.getMyAction()
        const lgs = await MarketComponentApi.getLeagues();
        const mks = await MarketComponentApi.getMarkets();
        const { event_ids, tournament_ids, match_ids } = MyOrdersHelpers.getContestsFromOrders(my_orders);
        let es = await MarketComponentApi.getEventsByEventIds(event_ids);
        let tourneys = await MarketComponentApi.getTournamentsByTournamentIds(tournament_ids);
        let mtchs = await MarketComponentApi.getMatchesByMatchIds(match_ids);

        setModuleData({ 
            ...module_data,
            orders: my_orders,
            events: es,
            tournaments: tourneys,
            leagues:lgs,
            markets: mks,
            matches:mtchs,
            loading:false
        })
    }

    const renderOrderStats = (data: { item:OrderStatsProps, index:number }) => {
        const { contest_title, contest_time_detail, league_id } = MyOrdersHelpers.getDetailsForStat(data.item, events, tournaments, matches);
        const league = leagues.find(l => l.league_id == league_id);
        const event_hedges = hedges.filter(h => h.event_id == data.item.event_id && h.event_type == data.item.event_type)

        return (
            <ContestOrderStatsCard
                league={league}
                hedges={event_hedges}
                player_order_stat={data.item}
                contest_title={contest_title}
                contest_time_detail={contest_time_detail}
                onPress={(pos) => console.log(pos)}
                market_type={market_type}
                onOrdersUpdate={(os) => console.log(os)}
            />
        )
    }


    const event_order_stats = MyOrdersHelpers.calcOrderStats(orders, player_id).sort((a,b) => {
        let a_open = a.open_amt
        let b_open = b.open_amt
        return b_open - a_open
    });

    const order_stats = MyOrdersHelpers.calcAllOrderStats(orders);
    const cl = market_type == 'FREE' ? 'E' : '$'
    return (
        <View>
            <View nativeID="order_stats" style={{ flexDirection:'row', justifyContent:'center', flexWrap:'wrap' }}>
                {order_stats.open_amt > 0 ?
                <View style={{ justifyContent:'space-between', width: (width/2) - 20, borderRadius:12, backgroundColor:Colors.shades.shade200, padding:20, margin:8}}>
                    <Text size={14} color={Colors.brand.cobalt}>Unfulfilled Order Amount</Text>
                    <Text size={16} weight='semibold' color={Colors.brand.cyan} style={{ marginTop:3 }}>{cl}{order_stats.open_amt.toFixed(2)}</Text>
                </View>
                :<></>}
                {order_stats.unresolved_stake > 0 ?
                <View style={{ justifyContent:'space-between', width: (width/2) - 20, borderRadius:12, backgroundColor:Colors.shades.shade200, padding:20, margin:8}}>
                    <Text size={14} color={Colors.brand.cobalt}>Unsettled Positions</Text>
                    <Text size={16} weight='semibold' color={Colors.brand.midnight} style={{ marginTop:3 }}>{order_stats.unresolved_stake.toFixed(2)} to win {cl}{order_stats.unresolved_potential_winnings.toFixed(2)}</Text>
                </View>
                :<></>}
                {order_stats.resolved_stake > 0 ?
                <View style={{ justifyContent:'space-between', width: (width/2) - 20, borderRadius:12, backgroundColor:Colors.shades.shade200, padding:20, margin:8}}>
                    <Text size={14} color={Colors.brand.cobalt}>Settled Positions</Text>
                    <Text size={16} weight='semibold' color={Colors.brand.midnight} style={{ marginTop:3 }}>{cl}{order_stats.resolved_stake.toFixed(2)}</Text>
                </View>
                :<></>}
                {order_stats.resolved_stake > 0 ?
                <View style={{ justifyContent:'space-between', width: (width/2) - 20, borderRadius:12, backgroundColor:Colors.shades.shade200, padding:20, margin:8}}>
                    <Text size={14} color={Colors.brand.cobalt}>Earnings</Text>
                    <Text size={16} weight='semibold' color={Colors.brand.midnight} style={{ marginTop:3 }}>{cl}{order_stats.earnings.toFixed(2)} ({(order_stats.roi * 100).toFixed(2)}%)</Text>
                </View>
                :<></>}
                {order_stats.delayed_cash > 0 ?
                <View style={{ justifyContent:'space-between', width: (width/2) - 20, borderRadius:12, backgroundColor:Colors.shades.shade200, padding:20, margin:8}}>
                    <Text size={14} color={Colors.brand.cobalt}>Delayed Cash Out</Text>
                    <Text size={16} weight='semibold' color={Colors.brand.midnight} style={{ marginTop:3 }}>{cl}{order_stats.delayed_cash.toFixed(2)}</Text>
                </View>
                :<></>}
            </View>
            <FlatList
                key={`${player_id}_order_stats`}
                data={event_order_stats}
                renderItem={renderOrderStats}
                keyExtractor={(item) => item.event_id.toString()}
            />
            <SocketManager
                subscribed_events={['V1_UPDATE_PLAYER_ORDER']}
                access_token={access_token}
                distinct_id={distinct_id}
                onConnect={() => {
                    if(needs_reload){ 
                        getDataFromServer() 
                        setNeedsReload(false);
                    }
                }}
                onDisconnect={() => setNeedsReload(true)}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'V1_UPDATE_PLAYER_ORDER':
                            if(ev.player_id != player_id){ return }
                            return setSocketOrder(ev.data)
                        default: return
                    }
                }}
            />
            {loading ?
            <View style={{ position:'absolute', top:0, left:0, right:0, bottom:0, backgroundColor:Colors.shades.black_faded, justifyContent:'center', alignItems:'center' }}>
                <ActivityIndicator
                    size='large'
                    color={Colors.brand.midnight}
                />
            </View>
            :<></>}
        </View>
    )

}

export default MyOrderList